import { Injectable } from '@angular/core';
import {
  catchError,
  EMPTY,
  filter,
  Observable,
  retry,
  Subject,
  tap,
  timer,
} from 'rxjs';
import { WebSocketSubject } from 'rxjs/webSocket';
import {
  Session,
  SessionStatusMessage,
} from '../models/horizon-payment-getaway.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class PCIPalService {
  readonly _incomingMessages = new Subject<Session>();
  get incomingMessages$(): Observable<Session> {
    return this._incomingMessages.asObservable();
  }

  private readonly heartbeatDelay = 30000;
  private readonly heartbeatMessage = {
    type: 'keepalive',
  };
  private readonly reconnectInterval = 5000;
  private readonly region = environment.pciPal.region;
  private readonly environment = environment.pciPal.environment;
  private readonly pcipalWebSocketUrl = `wss://${this.region}.${this.environment}.cloud/socket`;
  private readonly tenantId = environment.pciPal.tenantId;

  private socket$: WebSocketSubject<any>;
  private heartbeatInterval!: ReturnType<typeof setInterval>;

  constructor() {}

  public closeConnection(): void {
    this.stopHeartbeat();
    this.socket$.complete();
  }

  public getMessages(): Observable<Session> {
    return this.incomingMessages$;
  }

  public openConnection(accessToken: string, sessionId: string): void {
    this.socket$ = new WebSocketSubject(this.pcipalWebSocketUrl);
    this.sendMessage({
      type: 'auth',
      bearer: accessToken,
    });

    this.sendMessage({
      type: 'websession',
      register: sessionId,
      fullStatus: true,
    });

    this.connect();
  }

  public pcipalIframeUrl(sessionId: string): string {
    return `https://${this.region}.${this.environment}.cloud/session/${this.tenantId}/view/${sessionId}/framed`;
  }

  private connect(): void {
    this.socket$
      .pipe(
        retry({
          delay: (error, retryCount) => this.reConnect(error, retryCount),
        }),
        filter(
          (message: SessionStatusMessage) =>
            !!message && message.MessageType === 'SessionStatus'
        ),
        tap((message: SessionStatusMessage) =>
          this._incomingMessages.next(message.Session)
        ),
        catchError((error) => this.handleError(error))
      )
      .subscribe();

    this.initHeartbeat();
  }

  private initHeartbeat(): void {
    this.heartbeatInterval = setInterval(() => {
      this.sendMessage(this.heartbeatMessage);
    }, this.heartbeatDelay);
  }

  private reConnect(error, retryCount): Observable<number> {
    console.error('WebSocket error:', error);
    console.info(
      `WebSocket connection failed. Retrying in ${
        this.reconnectInterval / 1000
      } seconds... (Attempt ${retryCount})`
    );
    return timer(this.reconnectInterval);
  }

  private sendMessage(message: any): void {
    this.socket$.next(message);
  }

  private stopHeartbeat(): void {
    clearInterval(this.heartbeatInterval);
  }

  private handleError(error: any): Observable<never> {
    console.error('WebSocket error:', error);
    return EMPTY;
  }
}
